import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, CheckCircle2, FileText, Loader2, Sparkles, X, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

// Placeholder logo Digital Tech Solutions — sostituisci con URL asset ufficiale.
// Per ora usa un SVG inline stilizzato con brain + testo "Digital Tech solutions".
const DTS_LOGO_SVG_DATA_URL =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
    <defs>
      <linearGradient id='dts-brain' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0' stop-color='#f59e0b'/>
        <stop offset='0.5' stop-color='#8b5cf6'/>
        <stop offset='1' stop-color='#4c1d95'/>
      </linearGradient>
    </defs>
    <g fill='none' stroke='url(#dts-brain)' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'>
      <path d='M180 200c-30-4-52 12-52 42 0 26 20 42 40 42 -18 8 -28 26 -22 46 6 22 30 32 54 26 4 24 26 40 52 40'/>
      <path d='M332 200c30-4 52 12 52 42 0 26-20 42-40 42 18 8 28 26 22 46-6 22-30 32-54 26-4 24-26 40-52 40'/>
      <path d='M256 168v260'/>
      <path d='M220 236c-14 6-22 20-20 34'/>
      <path d='M292 236c14 6 22 20 20 34'/>
      <circle cx='220' cy='236' r='6' fill='url(#dts-brain)'/>
      <circle cx='292' cy='236' r='6' fill='url(#dts-brain)'/>
      <circle cx='256' cy='300' r='6' fill='url(#dts-brain)'/>
    </g>
    <text x='256' y='464' text-anchor='middle' font-family='Arial, sans-serif' font-weight='800' font-size='60' fill='#1e3a8a'>Digital Tech</text>
    <text x='256' y='500' text-anchor='middle' font-family='Arial, sans-serif' font-size='30' fill='#4b5563'>solutions</text>
  </svg>`);

/**
 * Card upload attestato Digital Tech Solutions con auto-approvazione.
 * Se onSuccess è fornita, viene invocata dopo un upload andato a buon fine.
 */
export default function DtsCertificatoUpload({ user, onSuccess }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState({
    unlocked: !!user?.corso_sbloccato_dts,
    fileId: user?.dts_certificato_id || null,
    filename: null,
    approvatoAt: null,
  });

  useEffect(() => {
    let ignore = false;
    axios
      .get(`${API}/api/me/dts-certificato/status`, { withCredentials: true })
      .then((r) => {
        if (ignore) return;
        setPreview({
          unlocked: !!r.data.corso_sbloccato_dts,
          fileId: r.data.dts_certificato_id,
          filename: r.data.dts_original_filename,
          approvatoAt: r.data.dts_approvato_at,
        });
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/me/dts-certificato/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({
        unlocked: true,
        fileId: r.data.id,
        filename: file.name,
        approvatoAt: r.data.approvato_at,
      });
      try {
        window.posthog?.capture?.("dts_certificato_uploaded", { filename: file.name, size: file.size });
      } catch { /* ignore */ }
      if (onSuccess) onSuccess(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore durante il caricamento. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = ""; // reset per permettere re-upload dello stesso file
  };

  // Stato: già sbloccato via DTS
  if (preview.unlocked) {
    return (
      <div
        className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-5 md:p-6"
        data-testid="dts-card-unlocked"
      >
        <div className="flex items-start gap-4">
          <img
            src={DTS_LOGO_SVG_DATA_URL}
            alt="Digital Tech Solutions"
            className="h-16 w-16 rounded-xl bg-white p-1.5 shadow-md"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">
              Corso sbloccato
            </div>
            <h3 className="mt-0.5 font-extrabold text-[#0a1f55]">
              Attestato Digital Tech Solutions verificato
            </h3>
            <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
              Il tuo attestato di partnership è stato caricato e approvato. Il Corso A.C.D.T. è
              accessibile.
              {preview.filename && (
                <span className="ml-1 text-slate-500">
                  · File: <span className="font-mono text-[12px]">{preview.filename}</span>
                </span>
              )}
            </p>
            {preview.fileId && (
              <a
                href={`${API}/api/files/${preview.fileId}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#0a1f55] hover:underline"
                data-testid="dts-view-file"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Visualizza attestato caricato
              </a>
            )}
          </div>
          <div className="h-10 w-10 shrink-0 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md">
            <CheckCircle2 className="h-5 w-5" />
          </div>
        </div>
      </div>
    );
  }

  // Stato: da caricare
  return (
    <div
      className="rounded-2xl border-2 border-[#8b5cf6]/30 bg-gradient-to-br from-[#f5f3ff] via-white to-[#fef3c7] p-5 md:p-7"
      data-testid="dts-upload-card"
    >
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        <div className="shrink-0">
          <img
            src={DTS_LOGO_SVG_DATA_URL}
            alt="Digital Tech Solutions"
            className="h-24 w-24 md:h-28 md:w-28 rounded-2xl bg-white p-2 shadow-lg"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#8b5cf6]/10 text-[#6d28d9] text-[10.5px] font-extrabold uppercase tracking-[0.18em]">
            <Sparkles className="h-3 w-3" />
            Partnership Riconosciuta
          </div>
          <h3 className="mt-2 text-[18px] md:text-[19px] font-extrabold text-[#0a1f55] leading-tight">
            Hai già seguito il corso Digital Tech Solutions?
          </h3>
          <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
            Carica il tuo attestato ufficiale <strong>Digital Tech Solutions</strong> per sbloccare
            automaticamente il Corso A.C.D.T. senza dover acquistare un pacchetto. L&apos;approvazione
            è <strong>istantanea</strong>.
          </p>

          <ul className="mt-3 space-y-1 text-[12.5px] text-slate-600">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              Formati accettati: <span className="font-mono text-[11.5px]">PDF, JPG, PNG, WEBP</span> (max 5 MB)
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              Sblocco immediato · nessuna approvazione manuale
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              Il tuo attestato resta visibile solo a te e all&apos;amministrazione
            </li>
          </ul>

          {error && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border-l-4 border-red-400 text-[12.5px] text-red-800">
              <X className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={onInputChange}
              className="hidden"
              data-testid="dts-file-input"
            />
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid="dts-upload-btn"
              className="bg-gradient-to-r from-[#8b5cf6] to-[#6d28d9] hover:from-[#7c3aed] hover:to-[#5b21b6] text-white font-extrabold h-11 px-5 shadow-lg"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Caricamento…
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Carica attestato Digital Tech Solutions
                </>
              )}
            </Button>
            <div className="text-[12px] text-slate-500 inline-flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              Un solo file per socio
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
